import { BaseParameterTypes } from '../../../shared/baseParams';
import {
  DPRESS_MAX,
  DPRESS_MIN,
  DPRESS_STEP,
  RPM_MAX,
  RPM_MIN,
  RPM_STEP,
  RPMTOT_MAX,
  RPMTOT_MIN,
  RPMTOT_STEP,
  WOB_MAX,
  WOB_MIN,
  WOB_STEP,
} from '../../const';
import type {
  HeatmapZFilterParams,
  HeatmapWidgetAxisParams,
  StepRangeKey,
  UnitOptionsKeys,
  ValueRangeKey,
  VisualMapAxisParams,
} from '../types';
import { AnalysisType, AnalysisTypeKey, ZFilterKey } from '../types';

export type XAxisKey = 'rpmAxisParams' | 'rpmtotAxisParams';
export type YAxisKey = 'wobAxisParams' | 'dpressAxisParams';
export type BaseAxisKey = XAxisKey | YAxisKey;
export type VisualMapAxisKey = 'ropAxisParams' | 'mseBitAxisParams' | 'mseSurfaceAxisParams';
export type AxisKey = BaseAxisKey | VisualMapAxisKey;
export type AxisParamsByKey = Record<BaseAxisKey, HeatmapWidgetAxisParams> &
  Record<VisualMapAxisKey, VisualMapAxisParams>;

// These keys also name the fields of the persisted, backwards-compatible state.
export const BASE_AXIS_NAMES = ['wob', 'dpress', 'rpm', 'rpmtot'] as const;
export const METRIC_NAMES = ['rop', 'mseBit', 'mseSurface'] as const;

export const AXIS_METADATA = {
  wobAxisParams: {
    parameterType: BaseParameterTypes.WEIGHT_ON_BIT,
    labelKey: 'Heatmap-range-wob',
  },
  dpressAxisParams: {
    parameterType: BaseParameterTypes.BIT_CONTACT_PRESSURE,
    labelKey: 'Heatmap-range-dpress',
  },
  rpmAxisParams: {
    parameterType: BaseParameterTypes.ROTARY_SPEED,
    labelKey: 'Heatmap-range-rpm',
  },
  rpmtotAxisParams: {
    parameterType: BaseParameterTypes.BIT_SPEED,
    labelKey: 'Heatmap-range-rpmtot',
  },
  ropAxisParams: {
    parameterType: BaseParameterTypes.RATE_OF_PENETRATION,
    labelKey: 'Heatmap-analysis-rop',
  },
  mseBitAxisParams: {
    parameterType: BaseParameterTypes.MECH_SPEC_ENERGY_BIT,
    labelKey: 'Heatmap-analysis-mse-bit',
  },
  mseSurfaceAxisParams: {
    parameterType: BaseParameterTypes.MECH_SPEC_ENERGY_SURFACE,
    labelKey: 'Heatmap-analysis-mse-surface',
  },
} satisfies Record<AxisKey, { parameterType: BaseParameterTypes; labelKey: string }>;

export const createAxisParamsFromConfig = (): AxisParamsByKey => ({
  wobAxisParams: {
    min: WOB_MIN,
    max: WOB_MAX,
    step: WOB_STEP,
    name: 'WOB',
    unit: { id: null, abbreviation: '' },
  },
  dpressAxisParams: {
    min: DPRESS_MIN,
    max: DPRESS_MAX,
    step: DPRESS_STEP,
    name: 'DPRESS',
    unit: { id: null, abbreviation: '' },
  },
  rpmAxisParams: {
    min: RPM_MIN,
    max: RPM_MAX,
    step: RPM_STEP,
    name: 'RPM',
    unit: { id: null, abbreviation: '' },
  },
  rpmtotAxisParams: {
    min: RPMTOT_MIN,
    max: RPMTOT_MAX,
    step: RPMTOT_STEP,
    name: 'RPM Total',
    unit: { id: null, abbreviation: '' },
  },
  ropAxisParams: {
    name: 'ROP',
    analysisType: AnalysisType.MEDIAN,
    unit: { id: null, abbreviation: '' },
  },
  mseBitAxisParams: {
    name: 'MSE Bit',
    analysisType: AnalysisType.MEDIAN,
    unit: { id: null, abbreviation: '' },
  },
  mseSurfaceAxisParams: {
    name: 'MSE Surface',
    analysisType: AnalysisType.MEDIAN,
    unit: { id: null, abbreviation: '' },
  },
});

// Legacy method keys remain supported; the store and Drawer use AxisKey internally.
export const VALUE_RANGE_AXIS_MAP: Record<ValueRangeKey, BaseAxisKey> = {
  wob: 'wobAxisParams',
  dpress: 'dpressAxisParams',
  rpm: 'rpmAxisParams',
  rpmtot: 'rpmtotAxisParams',
};
export const STEP_RANGE_AXIS_MAP: Record<StepRangeKey, BaseAxisKey> = VALUE_RANGE_AXIS_MAP;
export const UNIT_AXIS_MAP: Record<UnitOptionsKeys, AxisKey> = {
  wob: 'wobAxisParams',
  dpress: 'dpressAxisParams',
  rop: 'ropAxisParams',
  mse_bit: 'mseBitAxisParams',
  mse_surface: 'mseSurfaceAxisParams',
};
export const ANALYSIS_AXIS_MAP: Record<AnalysisTypeKey, VisualMapAxisKey> = {
  rop: 'ropAxisParams',
  mseBit: 'mseBitAxisParams',
  mseSurface: 'mseSurfaceAxisParams',
};
export const CURVE_AXIS_CONFIG = (Object.keys(AXIS_METADATA) as AxisKey[]).map((axisKey) => ({
  axisKey,
  parameterType: AXIS_METADATA[axisKey].parameterType,
}));
export const AXIS_LABEL_KEY_MAP = Object.fromEntries(
  Object.entries(AXIS_METADATA).map(([key, metadata]) => [key, metadata.labelKey]),
) as Record<AxisKey, string>;
export const UNIT_OPTIONS_CONFIG_MAP = Object.fromEntries(
  Object.entries(UNIT_AXIS_MAP).map(([key, axisKey]) => [
    key,
    { axisKey, parameterType: AXIS_METADATA[axisKey].parameterType },
  ]),
) as Record<UnitOptionsKeys, { axisKey: AxisKey; parameterType: BaseParameterTypes }>;

export const Z_FILTER_ANALYSIS_MAP: Record<ZFilterKey, AnalysisTypeKey> = {
  rotationRop: AnalysisTypeKey.ROP,
  rotationMse: AnalysisTypeKey.MSE_SURFACE,
  slideRop: AnalysisTypeKey.ROP,
  slideMse: AnalysisTypeKey.MSE_BIT,
};
export const Z_FILTER_KEYS = Object.values(ZFilterKey);
export const Z_FILTER_AXIS_MAP = Object.fromEntries(
  Z_FILTER_KEYS.map((key) => [key, ANALYSIS_AXIS_MAP[Z_FILTER_ANALYSIS_MAP[key]]]),
) as Record<ZFilterKey, VisualMapAxisKey>;
export const createZFilterParamsFromConfig = () =>
  Object.fromEntries(Z_FILTER_KEYS.map((key) => [key, { min: 0, max: null }])) as Record<
    ZFilterKey,
    HeatmapZFilterParams
  >;
